// components/SEO/ElectionsSEO.tsx
import React from 'react';
import { Helmet } from 'react-helmet-async';

interface ElectionsSEOProps {
    type: 'legislatives' | 'locales';
    title?: string;
    description?: string;
    image?: string;
    url?: string;
}

export const ElectionsSEO: React.FC<ElectionsSEOProps> = ({
                                                              type,
                                                              title,
                                                              description,
                                                              image = '/election-share.png',
                                                              url
                                                          }) => {
    // Données SEO spécifiques par type d'élection
    const seoData = {
        legislatives: {
            title: title || "Candidats UDB aux Élections Législatives 2025 | Union Démocratique des Bâtisseurs",
            description: description || "Découvrez tous nos candidats UDB aux élections législatives 2025 dans les 9 provinces du Gabon. 50+ candidats portent notre vision du Gabon nouveau dans 48 circonscriptions.",
            keywords: "UDB, élections législatives 2025, candidats Gabon, Union Démocratique des Bâtisseurs, politique Gabon, législatives, circonscriptions, provinces Gabon, candidature, démocratie, Brice Clotaire Oligui Nguema",
        },
        locales: {
            title: title || "Candidats UDB aux Élections Locales 2025 | Union Démocratique des Bâtisseurs",
            description: description || "Découvrez nos 500+ candidats UDB aux élections locales 2025 dans toutes les communes et départements des 9 provinces du Gabon. Votez pour le changement local.",
            keywords: "UDB, élections locales 2025, candidats locaux Gabon, Union Démocratique des Bâtisseurs, communes Gabon, départements, maires, conseillers municipaux, politique locale, Brice Clotaire Oligui Nguema",
        }
    };

    const currentData = seoData[type];
    const currentUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
    const imageUrl = image.startsWith('http') ? image : `${typeof window !== 'undefined' ? window.location.origin : 'https://www.udb.ga'}${image}`;

    // Données structurées JSON-LD
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": currentData.title,
        "description": currentData.description,
        "url": currentUrl,
        "image": imageUrl,
        "inLanguage": "fr-GA",
        "isPartOf": {
            "@type": "WebSite",
            "name": "Union Démocratique des Bâtisseurs",
            "url": typeof window !== 'undefined' ? window.location.origin : 'https://www.udb.ga'
        },
        "author": {
            "@type": "Organization",
            "name": "Union Démocratique des Bâtisseurs",
            "url": typeof window !== 'undefined' ? window.location.origin : 'https://www.udb.ga',
            "logo": {
                "@type": "ImageObject",
                "url": `${typeof window !== 'undefined' ? window.location.origin : 'https://www.udb.ga'}/logo512.png`
            }
        },
        "publisher": {
            "@type": "Organization",
            "name": "Union Démocratique des Bâtisseurs",
            "logo": {
                "@type": "ImageObject",
                "url": `${typeof window !== 'undefined' ? window.location.origin : 'https://www.udb.ga'}/logo512.png`
            }
        },
        "potentialAction": {
            "@type": "ReadAction",
            "target": currentUrl
        },
        "about": {
            "@type": "PoliticalParty",
            "name": "Union Démocratique des Bâtisseurs",
            "founder": {
                "@type": "Person",
                "name": "Brice Clotaire Oligui Nguema"
            }
        },
        "datePublished": "2024-12-01T00:00:00Z",
        "dateModified": new Date().toISOString()
    };

    return (
        <Helmet>
            {/* Titre de la page */}
            <title>{currentData.title}</title>

            {/* Balises meta de base */}
            <meta name="description" content={currentData.description} />
            <meta name="keywords" content={currentData.keywords} />
            <meta name="author" content="Union Démocratique des Bâtisseurs" />
            <meta name="robots" content="index, follow" />
            <meta name="language" content="fr" />
            <meta name="geo.region" content="GA" />
            <meta name="geo.country" content="Gabon" />

            {/* URL canonique */}
            <link rel="canonical" href={currentUrl} />

            {/* Open Graph / Facebook / WhatsApp */}
            <meta property="og:type" content="article" />
            <meta property="og:title" content={currentData.title} />
            <meta property="og:description" content={currentData.description} />
            <meta property="og:image" content={imageUrl} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:type" content="image/png" />
            <meta property="og:image:alt" content={`${currentData.title} - Union Démocratique des Bâtisseurs`} />
            <meta property="og:url" content={currentUrl} />
            <meta property="og:site_name" content="Union Démocratique des Bâtisseurs" />
            <meta property="og:locale" content="fr_GA" />
            <meta property="article:published_time" content="2024-12-01T00:00:00Z" />
            <meta property="article:modified_time" content={new Date().toISOString()} />
            <meta property="article:author" content="Union Démocratique des Bâtisseurs" />
            <meta property="article:section" content="Politique" />
            <meta property="article:tag" content="Élections 2025" />
            <meta property="article:tag" content="UDB" />
            <meta property="article:tag" content="Gabon" />

            {/* Twitter Cards */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@UDB_Gabon" />
            <meta name="twitter:creator" content="@UDB_Gabon" />
            <meta name="twitter:title" content={currentData.title} />
            <meta name="twitter:description" content={currentData.description} />
            <meta name="twitter:image" content={imageUrl} />
            <meta name="twitter:image:alt" content={`${currentData.title} - Union Démocratique des Bâtisseurs`} />

            {/* Préchargement de l'image de partage */}
            <link rel="preload" as="image" href={imageUrl} />

            {/* Données structurées JSON-LD */}
            <script type="application/ld+json">
                {JSON.stringify(structuredData)}
            </script>

            {/* Balises supplémentaires pour le référencement */}
            <meta name="theme-color" content="#333b92" />
            <meta name="apple-mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        </Helmet>
    );
};

// Hook personnalisé pour faciliter l'utilisation
export const useElectionsSEO = (type: 'legislatives' | 'locales', customData?: Partial<ElectionsSEOProps>) => {
    return {
        type,
        ...customData
    };
};
